import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    phoneNumber: state.components.contactUsForm.fieldValues["phoneNumber"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PhoneNumberStyles {
    phoneNumberTextField?: TextFieldPresentationProps;
}

export const phoneNumberStyles: PhoneNumberStyles = {
    phoneNumberTextField: {
        css: css`
            letter-spacing: 0.11em;
        `,
    },
};

const styles = phoneNumberStyles;

// Handles country codes and below formats:
// +1XXXXXXXXXX, XXXXXXXXXX, +1(XXX) XXX-XXXX, +1 XXX XXX XXXX, (XXX) XXX-XXXX, XXX-XXX-XXXX, XXX XXX-XXXX, XXX XXX XXXX
// +15062295727, 5062295727, +1(506) 229-5727, +1 506 229 5727, (506) 229-5727, 506-229-5727, 506 229-5727, 506 229 5727
// Above formats convert to 506-555-5727
const phoneRegex = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/;
const validatorRegex = /^\d{3}-\d{3}-\d{4}$/;
const PhoneNumber: React.FC<Props> = ({ fields, phoneNumber, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [phoneNumberErrorMessage, setPhoneNumberErrorMessage] = React.useState("");
    const [val, setVal] = React.useState(phoneNumber || "");

    React.useEffect(() => {
        setVal(phoneNumber || "");
    }, [phoneNumber]);

    React.useEffect(() => {
        contactFormContext.validators.phoneNumber = validatePhoneNumber;
    }, [phoneNumber]);

    React.useEffect(() => {
        contactFormContext.validators.phoneNumber = validatePhoneNumber;
    }, [isRequired]);

    const formatPhoneNumber = (phoneNumberString: string): string => {
        const cleaned = `${phoneNumberString}`.replace(/\D/g, "");
        const matchNoCountry = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        const matchWithCountry = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
        if (matchNoCountry) {
            return `${matchNoCountry[1]}-${matchNoCountry[2]}-${matchNoCountry[3]}`;
        }
        if (matchWithCountry) {
            return `${matchWithCountry[2]}-${matchWithCountry[3]}-${matchWithCountry[4]}`;
        }
        return phoneNumberString;
    };
    // (!phoneNumber && isRequired) || (phoneNumber && !phoneRegex.test(phoneNumber))
    const validatePhoneNumber = () => {
        const phoneNumberFormatted: string = formatPhoneNumber(phoneNumber || "");

        setFieldValue({
            key: "phoneNumber",
            value: phoneNumberFormatted,
        });

        const errorMessage =
            (!phoneNumber && isRequired) || (phoneNumber && !validatorRegex.test(phoneNumberFormatted))
                ? translate("Please enter a valid phone number including the area code.")
                : "";

        setPhoneNumberErrorMessage(errorMessage as string);

        return !errorMessage;
    };

    const phoneNumberChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setVal(event.target.value);
        setFieldValue({ key: "phoneNumber", value: event.target.value });
    };

    const phoneNumberBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        validatePhoneNumber();
    };

    return (
        <TextField
            {...styles.phoneNumberTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={val}
            onChange={phoneNumberChangeHandler}
            onBlur={phoneNumberBlurHandler}
            error={phoneNumberErrorMessage}
            type="tel"
        />
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PhoneNumber),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Phone Number",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "(###) ###-####",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};
export default widgetModule;
